import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';

import { serieFiltersAtom } from '../store/atoms/serieFiltersAtom';
import type { SerieFilters } from '../types/serieFilters';

/**
 * Hook para gerenciar os filtros (server-side) da listagem de Séries.
 *
 * @returns Filtros atuais e funções para atualizar e resetar.
 *
 * @example
 * ```tsx
 * const { filters, updateFilters, resetFilters } = useSerieFilters();
 * updateFilters({ page: 2 });
 * ```
 */
export function useSerieFilters() {
  const [filters, setFilters] = useAtom(serieFiltersAtom);
  const resetFilters = useResetAtom(serieFiltersAtom);

  /**
   * Atualiza parcialmente os filtros. Reseta para a primeira página, exceto
   * quando o próprio `page` é fornecido no `partial` (navegação de páginas).
   */
  const updateFilters = (partial: Partial<SerieFilters>) => {
    setFilters(prev => {
      const resetPage = 'page' in partial ? {} : { page: 1 };
      return { ...prev, ...resetPage, ...partial };
    });
  };

  return { filters, updateFilters, resetFilters } as const;
}
